import React from "react";
import { Phone, Mail, MapPin, MessageCircle, Clock, ArrowUpRight } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";
import SEO from "@/components/site/SEO";
import ConnectForm from "@/components/site/ConnectForm";
import { localBusinessJsonLd, siteMeta } from "@/lib/seo-config";

const MAPS_GOOGLE_URL = "https://maps.app.goo.gl/VB5dP4j43UFJkMdH";
// Embed URL — Google Maps search-based embed using our full address.
const MAPS_EMBED_SRC =
    "https://maps.google.com/maps?q=Pantone%209%2C%208%2027%20Street%20Al%20Qouz%20Ind%20Second%20Al%20Quoz%20Dubai&t=&z=16&ie=UTF8&iwloc=&output=embed";

const WhatsIcon = ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
        <path d="M19.05 4.91A9.83 9.83 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02Z" />
    </svg>
);

export default function ContactUs() {
    useScrollReveal();

    return (
        <main data-testid="page-contact-us" className="bg-[#FAFAF5]">
            <SEO
                title="Contact Us | 45DaySpaces Dubai — Office, Home, Retail & Restaurant Interiors"
                description="Talk to a 45DaySpaces specialist in Dubai. Visit our Al Quoz studio, WhatsApp us on +971 55 223 9345 or fill the form for a free consultation on office, home, retail and restaurant interiors."
                path="/contact-us"
                keywords="contact 45DaySpaces, interior design Dubai contact, fit-out Dubai consultation, Al Quoz interior studio"
                ogImage={siteMeta.ogImage}
                jsonLd={localBusinessJsonLd}
            />

            {/* Hero */}
            <section className="pt-16 pb-10 sm:pt-20">
                <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 text-center fade-up">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-3 py-1 text-[10px] font-bold tracking-[0.2em] text-black/70">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#7e9a00]" />
                        CONTACT 45DAYSPACES
                    </div>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[56px] font-extrabold leading-[1.05] tracking-[-0.03em]">
                        Let&apos;s design your next space.
                    </h1>
                    <p className="mt-5 text-base text-black/60 max-w-2xl mx-auto leading-relaxed">
                        Whether you&apos;re fitting out a new office in Business Bay, redesigning a villa in Al Barsha, or launching a café in Downtown — reach out and a 45DaySpaces specialist will get back within minutes on WhatsApp.
                    </p>
                </div>
            </section>

            {/* Contact grid: form + info */}
            <section className="pb-16">
                <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Left: Info cards */}
                        <div className="lg:col-span-5 space-y-4 fade-up">
                            <div className="rounded-[24px] bg-white border border-black/10 p-6 sm:p-7">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F2FFB0] text-[#5d6d00]">
                                        <MessageCircle className="h-5 w-5" />
                                    </span>
                                    <h2 className="font-display text-lg font-bold">WhatsApp us</h2>
                                </div>
                                <p className="mt-3 text-sm text-black/60 leading-relaxed">
                                    Fastest way to reach us. Send project details, size, or reference images and a specialist will reply the same day.
                                </p>
                                <a
                                    href="https://wa.me/971552239345"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-link"
                                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-white text-sm font-bold hover:bg-[#1FBD5B] transition-colors"
                                >
                                    <WhatsIcon className="h-4 w-4" />
                                    +971 55 223 9345
                                </a>
                            </div>

                            <div className="rounded-[24px] bg-white border border-black/10 p-6 sm:p-7">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F2FFB0] text-[#5d6d00]">
                                        <Mail className="h-5 w-5" />
                                    </span>
                                    <h2 className="font-display text-lg font-bold">Email</h2>
                                </div>
                                <p className="mt-3 text-sm text-black/60">For briefs, quotes, and detailed enquiries.</p>
                                <a
                                    href="mailto:hello@45dayspaces.ae"
                                    data-testid="contact-email-link"
                                    className="mt-3 inline-block text-sm font-semibold text-black underline underline-offset-4 hover:text-[#5d6d00]"
                                >
                                    hello@45dayspaces.ae
                                </a>
                            </div>

                            <div className="rounded-[24px] bg-white border border-black/10 p-6 sm:p-7">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F2FFB0] text-[#5d6d00]">
                                        <Phone className="h-5 w-5" />
                                    </span>
                                    <h2 className="font-display text-lg font-bold">Phone</h2>
                                </div>
                                <a
                                    href="tel:+971552239345"
                                    data-testid="contact-phone-link"
                                    className="mt-3 inline-block text-sm font-semibold text-black underline underline-offset-4 hover:text-[#5d6d00]"
                                >
                                    +971 55 223 9345
                                </a>
                            </div>

                            <div className="rounded-[24px] bg-[#111] p-6 sm:p-7 text-white">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#CCFF00] text-black">
                                        <MapPin className="h-5 w-5" />
                                    </span>
                                    <h2 className="font-display text-lg font-bold text-white">Visit our studio</h2>
                                </div>
                                <p className="mt-3 text-sm text-white/75 leading-relaxed">
                                    Pantone 9, 8 27 Street — Al Qouz Ind. Second,<br />Al Quoz, Dubai, UAE
                                </p>
                                <a
                                    href={MAPS_GOOGLE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-directions-link"
                                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#CCFF00] hover:opacity-90"
                                >
                                    Open in Google Maps
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                                <div className="mt-5 pt-5 border-t border-white/10 flex items-center gap-2 text-xs text-white/60">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span>Sun – Thu · 9:00 – 18:00 (GST)</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Form */}
                        <div className="lg:col-span-7 fade-up">
                            <div className="rounded-[28px] bg-white border border-black/10 p-6 sm:p-9">
                                <ConnectForm
                                    variant="page"
                                    prefill={{ source: "contact_page" }}
                                    showHeader={true}
                                    testIdPrefix="contact"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Map */}
            <section className="pb-24">
                <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
                    <div className="flex items-end justify-between gap-4 fade-up mb-5">
                        <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-[-0.02em]">Find us on the map</h2>
                        <a
                            href={MAPS_GOOGLE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-bold text-black hover:text-[#5d6d00]"
                        >
                            Open in Maps <ArrowUpRight className="h-4 w-4" />
                        </a>
                    </div>
                    <div className="rounded-[28px] overflow-hidden border border-black/10 shadow-[0_10px_40px_rgba(0,0,0,0.06)] fade-up">
                        <iframe
                            title="45DaySpaces Dubai — Google Maps location"
                            src={MAPS_EMBED_SRC}
                            width="100%"
                            height="520"
                            style={{ border: 0, display: "block" }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="contact-map-iframe"
                        />
                    </div>
                </div>
            </section>
        </main>
    );
}
